import {
  Body,
  Button,
  Heading,
  Tag,
  TagProps,
  Tooltip,
} from '@assembly-js/design-system';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type Stripe from 'stripe';

import { SUPPORT_EMAIL } from 'src/constants/externalUrls';
import { useFeatureFlags } from 'src/context';

export type AccountVerificationStatus =
  | 'pending'
  | 'failed'
  | 'unverified'
  | 'verified'
  | 'restricted'
  | 'onboarding';
export interface AccountStatusData {
  accountType: string;
  loginUrl: string;
  verificationStatus: AccountVerificationStatus | '';
  accountName: string;
  currency: string;
  accountCountry: string;
  businessName: string;
  /** Read only by the new-settings layout, to name the account as the prototype does. */
  accountLast4?: string;
  payoutSchedule?: Stripe.Account.Settings.Payouts.Schedule;
}
interface VerificationStatusProps {
  statusData: AccountStatusData;
  onConnectExpressClick: () => void;
  payoutsBlocked?: boolean;
}

interface VerificationStatusData {
  title: string;
  description: string;
  actionLabel?: string;
  tagColor?: TagProps['color'];
}

const VerificationStatusMapper: Record<
  AccountVerificationStatus,
  VerificationStatusData
> = {
  pending: {
    title: 'Verifying',
    description:
      'Please be patient as your banking information is being verified.',
    tagColor: 'info',
  },
  failed: {
    title: 'Failed to connect',
    description: `Your account could not be connected.`,
    actionLabel: 'Edit',
    tagColor: 'danger',
  },
  verified: {
    title: 'Successfully linked',
    description: `You're all set to bill clients and receive payments. Open your payout dashboard to manage your payment method, check your balance, and view upcoming payouts.`,
    actionLabel: 'Open Stripe Dashboard',
    tagColor: 'success',
  },
  unverified: {
    title: 'Not verified',
    description:
      'Information that you entered may not be correct or additional verification is required.',
    actionLabel: 'Verify now',
    tagColor: 'warning',
  },
  restricted: {
    title: 'Restricted',
    description: `Your account has been restricted due to a large number of failures when charging or managing payment methods. Please contact ${SUPPORT_EMAIL} for further assistance.`,
    tagColor: 'danger',
  },
  onboarding: {
    title: 'Onboarding',
    description: `Your account is being onboarded. Please complete the onboarding process to start receiving payments.`,
    actionLabel: 'Complete',
    tagColor: 'warning',
  },
};

export const VerificationStatus: React.FC<VerificationStatusProps> = ({
  statusData,
  onConnectExpressClick,
  payoutsBlocked = false,
}) => {
  const { t } = useTranslation();
  const {
    loginUrl,
    verificationStatus: status,
    accountName,
    currency,
    accountType,
  } = statusData;

  const handleEdit = () => {
    if (!loginUrl) return;
    window.open(loginUrl, '__target');
  };

  const actionLabel = status && VerificationStatusMapper[status].actionLabel;
  const { EnableNewSettings } = useFeatureFlags();

  if (EnableNewSettings && status) {
    return (
      <SettingsColumnVerificationStatus
        statusData={statusData}
        status={status}
        onConnectExpressClick={onConnectExpressClick}
        onOpenDashboard={handleEdit}
        payoutsBlocked={payoutsBlocked}
      />
    );
  }

  return status ? (
    <div>
      <div className="flex gap-2 items-center">
        <Heading size="lg">
          {`${accountName} (${currency.toUpperCase()})`}
        </Heading>
        <Tag
          color={VerificationStatusMapper[status].tagColor || 'default'}
          text={VerificationStatusMapper[status].title}
        />
      </div>
      <Body size="sm" className="text-gray-small mt-1">
        {VerificationStatusMapper[status].description}
      </Body>
      <div className="mt-2">
        {accountType === 'standard' &&
          (payoutsBlocked ? (
            <Tooltip content={t(($) => $.payout_account.ineligible)}>
              <span>
                <Button label="Connect Express" variant="secondary" disabled />
              </span>
            </Tooltip>
          ) : (
            <Button
              onClick={onConnectExpressClick}
              label="Connect Express"
              variant="secondary"
            />
          ))}
        {actionLabel &&
          loginUrl &&
          (accountType !== 'standard' || status === 'onboarding') && (
            <Button
              onClick={handleEdit}
              label={actionLabel}
              variant="secondary"
            />
          )}
      </div>
    </div>
  ) : null;
};

/** Stripe types the interval as a plain string; an unknown one shows no schedule. */
const getPayoutIntervalLabel = (
  interval: string,
  t: TFunction,
): string | undefined => {
  switch (interval) {
    case 'daily':
      return t(($) => $.payout_account.interval.daily);
    case 'weekly':
      return t(($) => $.payout_account.interval.weekly);
    case 'monthly':
      return t(($) => $.payout_account.interval.monthly);
    case 'manual':
      return t(($) => $.payout_account.interval.manual);
    default:
      return undefined;
  }
};

/**
 * The new-settings row: account and status on the left, the action on the
 * right. A verified account reads as the prototype has it ("Active", the
 * payout schedule, Change); every other status keeps its own copy and action.
 */
const SettingsColumnVerificationStatus: React.FC<{
  statusData: AccountStatusData;
  status: AccountVerificationStatus;
  onConnectExpressClick: () => void;
  onOpenDashboard: () => void;
  payoutsBlocked: boolean;
}> = ({
  statusData,
  status,
  onConnectExpressClick,
  onOpenDashboard,
  payoutsBlocked,
}) => {
  const { t } = useTranslation();
  const {
    loginUrl,
    accountName,
    currency,
    accountType,
    accountLast4,
    payoutSchedule,
  } = statusData;
  const statusCopy = VerificationStatusMapper[status];
  const isVerified = status === 'verified';

  const accountLabel = accountLast4
    ? `${accountName} ····${accountLast4}`
    : `${accountName} (${currency.toUpperCase()})`;
  const intervalLabel = payoutSchedule
    ? getPayoutIntervalLabel(payoutSchedule.interval, t)
    : undefined;
  const description = !isVerified
    ? statusCopy.description
    : payoutSchedule && intervalLabel
      ? t(($) => $.payout_account.verified_schedule, {
          interval: intervalLabel,
          days: payoutSchedule.delay_days,
        })
      : t(($) => $.payout_account.verified);
  const actionLabel = isVerified
    ? t(($) => $.payout_account.change)
    : statusCopy.actionLabel;

  const showsConnectExpress = accountType === 'standard';
  const showsDashboardAction =
    Boolean(actionLabel && loginUrl) &&
    (accountType !== 'standard' || status === 'onboarding');

  return (
    <div className="flex items-center gap-3.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Body
            size="sm"
            tag="span"
            className="font-medium leading-[22px] text-black-heading"
          >
            {accountLabel}
          </Body>
          <Tag
            color={statusCopy.tagColor || 'default'}
            text={
              isVerified ? t(($) => $.payout_account.active) : statusCopy.title
            }
          />
        </div>
        <Body
          size="xs"
          tag="div"
          className="mt-0.5 leading-[20px] text-gray-small"
        >
          {description}
        </Body>
      </div>
      {showsConnectExpress &&
        (payoutsBlocked ? (
          <Tooltip content={t(($) => $.payout_account.ineligible)}>
            <span>
              <Button label="Connect Express" variant="secondary" disabled />
            </span>
          </Tooltip>
        ) : (
          <Button
            onClick={onConnectExpressClick}
            label="Connect Express"
            variant="secondary"
          />
        ))}
      {showsDashboardAction && actionLabel && (
        <Button
          onClick={onOpenDashboard}
          label={actionLabel}
          variant="secondary"
        />
      )}
    </div>
  );
};
